import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Edit3, Trash2, Save, Loader2, X } from "lucide-react";
import AdminShell from "../components/AdminShell";
import ImageUploader from "../components/ImageUploader";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

const EMPTY = {
  full_name: "", title: "", company: "", bio: "",
  photo_url: "", website_url: "", linkedin_url: "",
  facebook_url: "", instagram_url: "",
  language: "vi", notes: "",
};

export default function AdminSpeakerLibrary() {
  const { lang } = useI18n();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [speakers, setSpeakers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2000); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/speakers", { headers, params: q ? { q } : {} });
      setSpeakers(r.data.speakers || []);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/admin/speakers/${id}`, payload, { headers });
      else await api.post("/admin/speakers", payload, { headers });
      setEditing(null);
      flash(L("Đã lưu", "Saved"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/speakers/${id}`, { headers });
      setConfirmDelete(null);
      flash(L("Đã xóa", "Deleted"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  return (
    <AdminShell
      testid="admin-speaker-library"
      subtitle={L("Thư Viện", "Library")}
      title={L("Diễn Giả Toàn Cầu", "Global Speakers")}
      actions={
        <button type="button" onClick={() => setEditing({ mode: "create", data: { ...EMPTY } })} data-testid="speaker-lib-add-btn"
          className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2 text-sm font-medium hover:bg-espresso-hover">
          <Plus className="w-4 h-4" /> {L("Thêm Diễn Giả", "Add Speaker")}
        </button>
      }
    >
      <div className="max-w-[1200px]">
        {toast && <div data-testid="speaker-lib-toast" className="mb-4 inline-block bg-[#E1EDE6] text-[#2D4A3E] rounded-xl px-4 py-2 text-sm font-medium">{toast}</div>}

        <div className="bg-white border border-espresso/10 rounded-3xl p-4 md:p-5 mb-6">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-3">
            <Search className="w-4 h-4 text-espresso-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Tìm theo tên, công ty, chức danh...", "Search by name, company, title...")} data-testid="speaker-lib-search"
              className="flex-1 bg-transparent text-sm focus:outline-none" />
            <button type="submit" data-testid="speaker-lib-search-btn" className="text-xs uppercase tracking-[0.22em] text-espresso font-semibold">
              {L("Tìm", "Search")}
            </button>
          </form>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-espresso-muted" /></div>
        ) : speakers.length === 0 ? (
          <div className="bg-white border border-dashed border-espresso/20 rounded-3xl p-12 text-center text-espresso-muted">
            {L("Chưa có diễn giả nào trong thư viện.", "No speakers in the library yet.")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {speakers.map((sp) => (
              <div key={sp.id} data-testid={`speaker-lib-card-${sp.id}`} className="bg-white border border-espresso/10 rounded-3xl p-5 flex gap-4">
                <div className="w-20 h-20 rounded-2xl bg-linen-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {sp.photo_url ? <img src={sp.photo_url} alt={sp.full_name} className="w-full h-full object-cover" /> : <span className="font-serif text-2xl text-espresso-muted">{sp.full_name?.[0] || "?"}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg text-espresso truncate">{sp.full_name}</div>
                  <div className="text-xs text-espresso-soft truncate">{[sp.title, sp.company].filter(Boolean).join(" · ")}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] mt-2 text-emerald2 font-semibold">{sp.event_count || 0} {L("sự kiện", "events")}</div>
                  <div className="mt-3 flex items-center gap-2">
                    <button type="button" onClick={() => setEditing({ mode: "edit", data: sp })} data-testid={`speaker-lib-edit-${sp.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(sp)} data-testid={`speaker-lib-delete-${sp.id}`} className="p-1.5 rounded-lg hover:bg-[#F5DADA] text-espresso-soft hover:text-[#7A2E2E]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <SpeakerEditDialog mode={editing.mode} initial={editing.data} onClose={() => setEditing(null)}
          onSave={(payload) => save(payload, editing.mode === "edit" ? editing.data.id : null)} L={L} />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={L("Xóa diễn giả?", "Delete speaker?")}
          body={L(`Diễn giả sẽ bị xóa khỏi thư viện. Mọi liên kết sự kiện cũng sẽ bị xóa. Tiếp tục?`, `This will remove the speaker from the library and all their event links. Continue?`)}
          onCancel={() => setConfirmDelete(null)} onConfirm={() => remove(confirmDelete.id)} L={L}
        />
      )}
    </AdminShell>
  );
}

function SpeakerEditDialog({ mode, initial, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name?.trim()) return;
    setSaving(true);
    await onSave({
      full_name: form.full_name.trim(),
      title: form.title || null, company: form.company || null, bio: form.bio || null,
      photo_url: form.photo_url || null, website_url: form.website_url || null,
      facebook_url: form.facebook_url || null, instagram_url: form.instagram_url || null,
      linkedin_url: form.linkedin_url || null,
      language: form.language || "vi", notes: form.notes || null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="speaker-lib-dialog">
      <form onSubmit={submit} className="w-full max-w-2xl bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] max-h-[92vh] flex flex-col">
        <div className="px-6 md:px-8 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl md:text-3xl text-charcoal">{mode === "edit" ? L("Chỉnh sửa Diễn Giả", "Edit Speaker") : L("Thêm Diễn Giả", "Add Speaker")}</h3>
          <button type="button" onClick={onClose} data-testid="speaker-lib-dialog-close" className="text-warmgray-500 hover:text-charcoal"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 md:px-8 py-6 overflow-y-auto grid md:grid-cols-2 gap-4">
          <Input label={L("Tên đầy đủ *", "Full name *")} value={form.full_name} onChange={(v) => set("full_name", v)} testId="speaker-lib-full-name" required />
          <Input label={L("Chức danh", "Title")} value={form.title} onChange={(v) => set("title", v)} testId="speaker-lib-title" />
          <Input label={L("Công ty", "Company")} value={form.company} onChange={(v) => set("company", v)} testId="speaker-lib-company" />
          <div className="md:col-span-2">
            <ImageUploader
              label={L("Ảnh diễn giả", "Speaker photo")}
              value={form.photo_url}
              onChange={(url) => set("photo_url", url)}
              subfolder="speakers"
              aspect="square"
              testId="speaker-lib-photo"
              hint={L("Ảnh vuông (1:1) hoặc chân dung hiển thị đẹp nhất. Tối đa 10MB.", "Square (1:1) or portrait works best. Max 10MB.")}
            />
          </div>
          <Textarea label={L("Tiểu sử", "Bio")} value={form.bio} onChange={(v) => set("bio", v)} rows={3} testId="speaker-lib-bio" className="md:col-span-2" />
          <Input label="Website" value={form.website_url} onChange={(v) => set("website_url", v)} testId="speaker-lib-website" />
          <Input label="LinkedIn" value={form.linkedin_url} onChange={(v) => set("linkedin_url", v)} testId="speaker-lib-linkedin" />
          <Input label="Facebook" value={form.facebook_url} onChange={(v) => set("facebook_url", v)} testId="speaker-lib-facebook" />
          <Input label="Instagram" value={form.instagram_url} onChange={(v) => set("instagram_url", v)} testId="speaker-lib-instagram" />
          <Textarea label={L("Ghi chú nội bộ", "Internal notes")} value={form.notes} onChange={(v) => set("notes", v)} rows={2} testId="speaker-lib-notes" className="md:col-span-2" />
        </div>
        <div className="px-6 md:px-8 py-4 border-t border-warmgray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal" data-testid="speaker-lib-dialog-cancel">{L("Hủy", "Cancel")}</button>
          <button type="submit" disabled={!form.full_name?.trim() || saving} data-testid="speaker-lib-dialog-save" className="inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {L("Lưu", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmDialog({ title, body, onCancel, onConfirm, L }) {
  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/40 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6" data-testid="confirm-dialog">
        <h3 className="font-serif text-2xl text-charcoal">{title}</h3>
        <p className="mt-2 text-sm text-warmgray-700">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} data-testid="confirm-cancel" className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal">{L("Hủy", "Cancel")}</button>
          <button type="button" onClick={onConfirm} data-testid="confirm-confirm" className="bg-[#7A2E2E] text-white rounded-full px-4 py-2 text-sm font-medium hover:opacity-90">{L("Xóa", "Delete")}</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testId, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}
